import React from "react";
import { Filter, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { statusOptions } from "@/utils/statusMappings";

interface MultiStatusFilterProps {
  selectedStatuses: string[];
  onStatusChange: (statuses: string[]) => void;
  className?: string;
}

const MultiStatusFilter = ({ 
  selectedStatuses, 
  onStatusChange,
  className = ""
}: MultiStatusFilterProps) => {
  const handleToggleStatus = (statusValue: string) => {
    if (selectedStatuses.includes(statusValue)) {
      onStatusChange(selectedStatuses.filter(s => s !== statusValue));
    } else {
      onStatusChange([...selectedStatuses, statusValue]);
    }
  };

  const handleSelectAll = () => {
    const allStatuses = statusOptions
      .filter(opt => opt.value !== "all")
      .map(opt => opt.value);
    onStatusChange(allStatuses);
  };

  const handleClearAll = () => {
    onStatusChange([]);
  };

  const getButtonLabel = () => {
    if (selectedStatuses.length === 0) {
      return "Alle statussen";
    }
    if (selectedStatuses.length === 1) {
      return "1 status";
    }
    return `${selectedStatuses.length} statussen`;
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          className={`whitespace-nowrap ${className}`}
        >
          <Filter className="h-4 w-4 mr-2" />
          {getButtonLabel()}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="start">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-sm">Status filters</h4>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSelectAll}
                className="h-7 text-xs"
              >
                Alles
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAll}
                className="h-7 text-xs"
              >
                Wissen
              </Button>
            </div>
          </div>
          
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {statusOptions
              .filter(option => option.value !== "all")
              .map((option) => (
                <div
                  key={option.value}
                  className="flex items-center space-x-2 py-1.5 px-2 rounded-md hover:bg-accent cursor-pointer"
                  onClick={() => handleToggleStatus(option.value)}
                >
                  <Checkbox
                    id={option.value}
                    checked={selectedStatuses.includes(option.value)}
                    onCheckedChange={() => handleToggleStatus(option.value)}
                  />
                  <label
                    htmlFor={option.value}
                    className="text-sm cursor-pointer flex-1"
                  >
                    {option.label}
                  </label>
                  {selectedStatuses.includes(option.value) && (
                    <Check className="h-4 w-4 text-primary" />
                  )}
                </div>
              ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default MultiStatusFilter;
